<script setup lang="ts">
import { storeToRefs } from 'pinia';
import LoyaltyHubCard from './Card.vue';
import LoyaltyHubLevelCard from './LevelCard.vue';
import LoyaltyHubCashbackSheet from './CashbackSheet.vue';
import LoyaltyHubTerms from './Terms.vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';
import { useRouletteStore } from '@/stores/roulette';
import { useDailyCheckinStore } from '@/stores/daily-checkin';
import { loyaltyHubHeaderArt } from '@/utils/loyalty-hub';
import type { LoyaltyHubCard as LoyaltyHubCardType } from '~/types/loyalty-hub';

const store = useLoyaltyHubStore();
const rouletteStore = useRouletteStore();
const promocodeStore = usePromocodeStore();
const dailyCheckinStore = useDailyCheckinStore();
const route = useRoute();
// `cards` já vem ordenado por `priority` (o store não mexe em `content`).
const { isOpen, content, cards, desktopOrder, status } = storeToRefs(store);
const { isUnavailable: checkinUnavailable } = storeToRefs(dailyCheckinStore);

// Defesa: `enabled: true` no contrato promete que o card abre alguma coisa, e
// quem cumpre a promessa é o core. Sem período publicado, com a trilha vazia ou
// com a API fora do ar, o card do check-in passa a se comportar EXATAMENTE como
// `enabled: false` — véu, ribbon de "Em breve" e clique inerte, tudo já no
// Card.vue. Melhor um card visivelmente trancado que um modal que abre em
// "indisponível".
//
// Jogador fora da promoção (`is_eligible: false`) NÃO cai aqui, de propósito: a
// store dá a ele um estado próprio ('ineligible') que o `isUnavailable` não
// inclui, então o card segue normal e a resposta vem ao abrir — do mesmo jeito
// que a roletinha trata o mesmo jogador. Trancar era esconder a promoção de quem
// ainda vai entrar nela; ver o comentário do `isUnavailable` na store.
//
// Casa por `action`, não por `id`: quem decide isso é o destino do clique, e o
// id vem do contrato (o core pode renomear).
//
// Enquanto o aquecimento do onOpened() não responde, `checkinUnavailable` é
// false e o card segue ativo — ver o comentário do `isUnavailable` na store.
// Cartão de nível (elo + XP do jogador) no topo da lista: só com usuário
// logado e a gamificação de níveis ligada — sem isso o hub segue como era.
const { isGamificationLevelsActived } = storeToRefs(useSmarticoConfigs());
const { isAuthenticated } = storeToRefs(useAuthStore());

const showLevelCard = computed(
  () => isAuthenticated.value && isGamificationLevelsActived.value,
);

function openLevels() {
  close();
  navigateTo(localeLink('/vip/levels'));
}

const displayCards = computed(() =>
  cards.value.map((card) =>
    card.action === 'checkin' && checkinUnavailable.value
      ? { ...card, enabled: false }
      : card,
  ),
);

// `visible` mantém o overlay montado até o slide-down terminar; `sheetOpen`
// dispara a animação (mesma mecânica da roletinha).
const visible = ref(false);
const sheetOpen = ref(false);

// Header é UMA imagem única (louros + "Seja bem-vindo a Clube Fidelidade" +
// hero), exportada do Figma — não remontamos texto/louros no DOM.
const headerImage = computed(() => loyaltyHubHeaderArt(content.value));

// Desktop tem enquadramento próprio no Figma. Contrato manda quando existir;
// senão cai no caminho por convenção (mesmo esquema do `background?` dos cards).
const headerImageDesktop = computed(() =>
  loyaltyHubHeaderArt(content.value, true),
);

const headerAlt = computed(() =>
  content.value ? `${content.value.greeting} ${content.value.title}` : '',
);

// Fade-in da arte do header (mesma mecânica do Card): a caixa é reservada por
// `aspect-ratio` e a imagem aparece por cima dela quando chega. Diferente do
// Card, o <img> do header nasce e morre com o overlay enquanto ESTE componente
// segue montado — daí o reset ao fechar, pra reabertura não começar já em
// `is-loaded` (o que faria a arte fria pular direto pra opacidade 1).
const headerLoaded = ref(false);

// Trava o scroll do fundo enquanto o hub está visível (abre até o fim do
// slide-down). Usa o composable com contador de referência do projeto — o
// manual `body.style.overflow` era sobrescrito pelo apply() de outros locks.
useBodyScrollLock(visible);

watch(
  isOpen,
  async (opened) => {
    if (opened) {
      visible.value = true;
      await nextTick();
      sheetOpen.value = true;
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

// Fecha o hub ao navegar de rota (ex.: escolher um cashback que abre
// /promotions/...), pra não ficar pendurado por cima da nova rota.
// Observa `path` (não `fullPath`): mudança só de query — como a limpeza do
// deep-link `?open=clube-fidelidade` que o layout faz via router.replace logo
// após abrir — não deve fechar o hub (senão ele abre e fecha na hora).
watch(
  () => route.path,
  () => {
    if (isOpen.value) store.close();
  },
);

const scrollEl = ref<HTMLElement | null>(null);

// No desktop o painel não cobre a tela toda: com o cursor fora dele a roda do
// mouse rola a página atrás (o lock de body não segura esse caso). Cancela o
// wheel que não nasce dentro da lista; o `overscroll-behavior: contain` do
// .lh-scroll já cobre o encadeamento quando a lista chega no fim.
function onWheel(event: WheelEvent) {
  const el = scrollEl.value;

  if (!el || !el.contains(event.target as Node)) event.preventDefault();
}

// Conteúdo carregado só após o slide-up (não compete com a animação).
function onOpened() {
  store.init();

  // Aquece a trilha do check-in enquanto o jogador lê os cards: o modal dele
  // abre daqui, e o board fica no cache do handler (5 min) pro clique subir o
  // painel já com conteúdo em vez de skeleton.
  //
  // `silent` é o que faz disso um aquecimento e não uma abertura: não pisca
  // 'loading' e, principalmente, NÃO deixa erro de rede gravado no estado —
  // senão uma falha que ninguém pediu apareceria como "não foi possível
  // carregar" no primeiro clique do jogador.
  dailyCheckinStore.load({ silent: true });
}

function onClosed() {
  visible.value = false;
  headerLoaded.value = false;
}

function close() {
  store.close();
}

function handleSelect(card: LoyaltyHubCardType) {
  switch (card.action) {
    case 'roulette':
      // Empilha a roletinha POR CIMA do hub (z maior); o hub segue aberto atrás.
      rouletteStore.open();
      break;

    case 'cashback':
      // Empilha o "Escolha seu Cashback" (formato bottom-sheet da roletinha).
      store.openCashback();
      break;

    case 'checkin':
      // Cinto além do suspensório do `displayCards`: se o veredito chegar entre
      // o ponteiro descer e o clique disparar, o card ainda está ativo no DOM.
      if (checkinUnavailable.value) return;

      // Empilha o "Recompensas Diárias" POR CIMA do hub (z-9996); o hub segue
      // aberto atrás, igual a roletinha.
      dailyCheckinStore.open();
      break;

    case 'promocode':
      // Empilha o modal do Promocode (z-9997) POR CIMA do hub, igual à roletinha
      // e ao check-in — o hub segue aberto atrás.
      promocodeStore.open();
      break;

    case 'route':
      if (!card.target) {
        // TODO: destino ainda não definido (aguardando decisão de rota).
        console.warn(`[LoyaltyHub] card "${card.id}" sem target de rota`);

        return;
      }

      close();
      navigateTo(localeLink(card.target));
      break;

    case 'blog':
      if (!card.target) return;
      close();
      navigateTo(localeLink(`/blog/${card.target}`));
      break;

    case 'external':
      if (!card.target) return;
      window.open(card.target, '_blank', 'noopener');
      break;

    default:
      break;
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="lh-overlay"
      @dragstart.prevent
      @wheel="onWheel"
    >
      <div
        class="lh-backdrop"
        :class="{ 'lh-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        name="lh-slide-up"
        @after-enter="onOpened"
        @after-leave="onClosed"
      >
        <div
          v-if="sheetOpen"
          class="lh-screen"
        >
          <button
            aria-label="Fechar"
            class="lh-close"
            type="button"
            @click="close"
          >
            <NuxtIcon name="close" />
          </button>

          <div
            ref="scrollEl"
            class="lh-scroll"
          >
            <!-- Header: imagem única (louros + texto + hero) -->
            <header
              v-if="headerImage"
              class="lh-header"
            >
              <picture>
                <source
                  media="(min-width: 1024px)"
                  :srcset="headerImageDesktop"
                />
                <img
                  :alt="headerAlt"
                  class="lh-header__img"
                  :class="{ 'is-loaded': headerLoaded }"
                  decoding="async"
                  fetchpriority="high"
                  :src="headerImage"
                  @error="headerLoaded = true"
                  @load="headerLoaded = true"
                />
              </picture>
            </header>

            <!-- Cards -->
            <div
              v-if="status === 'loading' && !content"
              class="lh-loading"
            >
              <NuxtIcon name="loading" />
            </div>

            <template v-else>
              <!-- Cartão de nível: o elo do jogador vestindo o TierSkin, acima
                   dos cards do contrato (desktop e mobile). -->
              <ClientOnly>
                <LoyaltyHubLevelCard
                  v-if="showLevelCard"
                  class="lh-level"
                  @select="openLevels"
                />
              </ClientOnly>

              <div class="lh-cards">
                <LoyaltyHubCard
                  v-for="(card, i) in displayCards"
                  :key="card.id"
                  :card="card"
                  :desktop-index="desktopOrder[card.id]"
                  :index="i"
                  :total="displayCards.length"
                  @select="handleSelect"
                />
              </div>

              <!-- Regulamento do Programa de Fidelidade: accordion fechado, no
                   fim da lista (junto com os cards, não durante o loading). -->
              <LoyaltyHubTerms class="lh-terms-slot" />
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>

  <!-- Cashback ("Escolha seu Cashback") no formato bottom-sheet da roletinha,
       empilhado por cima do hub. -->
  <LoyaltyHubCashbackSheet />
</template>

<!-- Slide-up/fade sem scope: @keyframes contornam o reset global de transition
     (mesma estratégia da roletinha). -->
<style>
.lh-slide-up-enter-active {
  animation: lh-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.lh-slide-up-leave-active {
  animation: lh-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes lh-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
.lh-overlay {
  @apply fixed inset-0 z-[9990] flex flex-col justify-end select-none;

  img {
    -webkit-user-drag: none;
  }
}

.lh-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  animation: lh-fade-in 0.25s ease forwards;
}

.lh-backdrop--out {
  animation: lh-fade-in 0.24s ease reverse forwards;
}

@keyframes lh-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.lh-screen {
  // Fundo no bg-primary do tema (#020716) — os cantos/bleed transparentes dos
  // cards fundem sem faixa mais clara.
  @apply relative z-10 flex flex-col w-full bg-bg-primary;

  max-width: 480px;
  // Diferente da roletinha: ocupa a tela TODA (vai até o topo, sem folga acima).
  height: 100dvh;
  margin: 0 auto;

  // `clip` (não `hidden`) mata o scroll fantasma do sheet — mesma estratégia da
  // roletinha; só a lista interna (.lh-scroll) rola.
  overflow: clip;

  // Contém o `mix-blend-mode: screen` da aura do header: sem isolar, ela
  // blendaria com o backdrop/página atrás do painel.
  isolation: isolate;
}

.lh-close {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 12px);
  right: 12px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;

  :deep(svg) {
    width: 16px;
    height: 16px;
  }
}

.lh-scroll {
  // `relative`/z-index colocam o conteúdo acima das camadas de glow do fundo.
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  // Sem encadear o scroll pro fundo (sem rubber-band vazando pro body).
  overscroll-behavior: contain;
  padding: env(safe-area-inset-top, 0px) 16px
    calc(env(safe-area-inset-bottom, 0px) + 20px);
  -webkit-overflow-scrolling: touch;
}

/* ---------- header (imagem única) ---------- */
.lh-header {
  position: relative;
  // `z-index: 0` (e não `auto`) faz do header um contexto de empilhamento: o
  // `z-index: 1` da arte passa a valer só DENTRO dele. Sem isso a arte
  // competia direto com os cards — que são `position: relative` sem z-index —
  // e ganhava por ter z-index maior, ficando NA FRENTE deles. Agora o header
  // pinta como um bloco só, antes dos cards na ordem do DOM: a base da pilha
  // passa ATRÁS da primeira linha do grid.
  z-index: 0;
  display: flex;
  justify-content: center;
  // Folga só no topo (pro botão fechar); embaixo cola nos cards. Sem folga
  // LATERAL de propósito: a largura da arte é uma % (ver `.lh-header picture`)
  // e ela tem que cair sobre a largura dos cards — com os 8px de cada lado que
  // havia aqui, os 69,7% viravam 66,5% da célula e a arte saía 11px menor.
  padding: 12px 0 0;

  // Prende a aura na caixa do header. As duas camadas são ancoradas em
  // `bottom: 0`, então o `filter: blur()` delas vazava pra FORA da caixa — o
  // ::after (blur 22px) descia ~50px por cima da primeira linha do grid e
  // aparecia no vão entre os cards e no bleed transparente da arte deles
  // (medido: 82 de luminância contra 30 do fundo do painel; some só ~50px
  // abaixo). Mais visível no desktop, onde o grid é 2-col e o vão central cai
  // bem embaixo da base da aura.
  // Sem costura: a parte cortada fica toda atrás da arte — o alcance
  // horizontal do ::after (80% de raio 50% em 700px = 280px) bate com a
  // metade da arte (560/2), e nas abas o ::before já chega em transparente
  // antes da borda de baixo.
  // `clip` e não `hidden`: `hidden` viraria scroll container (mesmo motivo do
  // .lh-screen).
  overflow: clip;
}

// Aura azul neon subindo do rodapé do header: separa a arte (escura na base,
// onde ficam os presentes) do fundo chapado do painel.
// É o ÚNICO efeito de fundo do hub — o resto do painel fica limpo, de propósito.
// Sem leque nem raio: a forma é um gradiente ancorado na base mascarado por
// ruído em mosaico com limiar suave (gain 1.55 / bias 0) — o ruído dá TEXTURA à
// aura em vez de recortá-la em blobs. A única animação é o PAN do mosaico
// subindo; sem pulso, sem giro, sem escala.
// ARMADILHA DO RAIO DO GRADIENTE: a última parada é 88%, então o raio horizontal
// tem que ser <= 50/0,88 ≈ 56% pra cor chegar em transparente ANTES da borda da
// caixa. Com os 78% que eu tinha, na borda o gradiente ainda estava em 64% do
// raio (alpha ~0,16) e a caixa cortava seco — era o vinco que aparecia nas
// laterais. Medido: dentro do limite, o maior salto de brilho na linha cai pra
// ~3 níveis, e no centro (textura do ruído), não nas bordas.
.lh-header::before,
.lh-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  // Acompanha a largura da arte (com uma folga pro glow vazar), não a do
  // container: no desktop o header tem 1119px e a arte só 560px.
  width: 100%;
  z-index: 0;
  pointer-events: none;
  // Centralização ESTÁTICA: com o pulso, esse translate vivia dentro dos
  // keyframes; sem ele a aura escorregaria meia largura pra direita.
  transform: translateX(-50%);
  mix-blend-mode: screen;
}

// O fogo.
// TETO DA ALTURA: o alcance visível é `height × raioY × última parada`. Passando
// de 100% da caixa do header o glow bate no `overflow: clip` dela e corta reto no
// topo. Com 86% × 104% × 0,88 ≈ 79% ele sobe até a metade de cima da arte e ainda
// morre com folga dentro da caixa. Soltar a aura do header pra ir além não vale:
// o topo do painel fica logo acima dele, o ganho seria de ~8%.
.lh-header::before {
  max-width: 480px;
  height: 86%;
  background: radial-gradient(
    56% 104% at 50% 100%,
    rgba(180, 205, 255, 1) 0%,
    rgba(105, 140, 255, 0.72) 44%,
    rgba(35, 60, 240, 0.32) 70%,
    transparent 88%
  );
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='360' height='360'%3E%3Cfilter id='f' x='0' y='0' width='100%25' height='100%25'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.014' numOctaves='3' seed='7' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 1.55 0 0 0 0'/%3E%3C/filter%3E%3Crect width='360' height='360' filter='url(%23f)'/%3E%3C/svg%3E");
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='360' height='360'%3E%3Cfilter id='f' x='0' y='0' width='100%25' height='100%25'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.014' numOctaves='3' seed='7' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 1.55 0 0 0 0'/%3E%3C/filter%3E%3Crect width='360' height='360' filter='url(%23f)'/%3E%3C/svg%3E");
  -webkit-mask-repeat: repeat;
  mask-repeat: repeat;
  -webkit-mask-size: 360px 360px;
  mask-size: 360px 360px;
  filter: blur(5px);
  // `mask-position` não é composta na GPU; o hint isola o repaint nesta camada.
  will-change: mask-position;
  animation: lh-header-fire 9s linear infinite;
}

// Base acesa, parada — só firma o pé da aura.
.lh-header::after {
  max-width: 400px;
  height: 46%;
  background: radial-gradient(
    50% 98% at 50% 100%,
    rgba(195, 218, 255, 0.9) 0%,
    rgba(55, 90, 250, 0.46) 46%,
    transparent 80%
  );
  filter: blur(22px);
}

// Sobe exatamente um tile: o último frame é idêntico ao primeiro.
@keyframes lh-header-fire {
  from {
    -webkit-mask-position: 0 0;
    mask-position: 0 0;
  }
  to {
    -webkit-mask-position: 0 -360px;
    mask-position: 0 -360px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .lh-header::before {
    animation: none;
  }
}

// O <picture> é o flex item (o <img> vive dentro dele), então a largura tem que
// ser travada nele — senão o item encolhe pelo tamanho intrínseco da arte.
// `relative`/z-index colocam a arte por cima da aura.
// Largura em % (e não em px) porque é assim que ela é FIEL ao Figma: o frame
// mobile do header tem 374 de largura — a mesma dos cards, ou seja a largura de
// conteúdo do painel — e a arte recortada no bbox mede 260,7 dentro dele. Logo
// 260,7/374 = 69,7% da largura dos cards, em qualquer tela.
.lh-header picture {
  position: relative;
  z-index: 1;
  display: block;
  width: 69.7%;
}

// Mesma reserva de caixa dos cards (a razão do `auto` está comentada no
// Card.vue): a arte do header mede 782x461 no mobile e 1510x683 no desktop.
// Aqui ela pesava dobrado — o header é o primeiro bloco da lista, então crescer
// de 0 pra altura final empurrava TODOS os cards de uma vez.
.lh-header__img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: auto 782 / 461;
  opacity: 0;
}

.lh-header__img.is-loaded {
  animation: lh-header-img-in 0.18s ease forwards;
}

@keyframes lh-header-img-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* ---------- cards ---------- */
// Grid de 6 colunas serve mobile E desktop (o span de cada card muda por
// breakpoint, no Card.vue): mobile full=span6/half=span3; desktop 2-col=span3 e
// a última linha (3 cards) span2. A arte transborda pra cima dentro do gap.
// Sem z-index próprio: o cartão fica no fluxo — o emblema flutuante ainda
// sobe sobre o header (vizinho ANTERIOR pinta por cima), e os cards de baixo
// (vizinhos POSTERIORES) pintam por cima do cartão, não atrás.
.lh-level {
  margin: 0 0 16px;
}

.lh-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  // Alinha os cards pelo topo da célula (mesma altura de corpo) — evita que o
  // grid estique um card e desnivele a linha.
  align-items: start;
  gap: 16px;
  padding-top: 0;
}

// Mesma folga do `gap` do grid: o accordion vira "a linha seguinte" dos cards.
// A classe chega pela raiz do Terms.vue (que herda o escopo deste componente).
.lh-terms-slot {
  margin-top: 16px;
}

.lh-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: #fff;

  :deep(svg) {
    width: 32px;
    height: 32px;
    animation: lh-spin 1s linear infinite;
  }
}

@keyframes lh-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (min-width: 1024px) {
  .lh-overlay {
    @apply justify-center;
  }

  .lh-screen {
    // Desktop: painel largo pro grid 2-col (Figma tem ~1119px de conteúdo).
    max-width: 1160px;
    height: min(88dvh, 900px);
    border-radius: 22px;
  }

  // Header proporcional ao painel largo (no mobile a arte fica em 69,7%).
  .lh-header {
    padding-top: 20px;
  }

  // Mesma conta do mobile no frame desktop do Figma: 503,3 de arte em 1119 de
  // conteúdo = 45%. O enquadramento desktop é mais largo, então a arte ocupa
  // proporcionalmente MENOS do painel do que no mobile.
  .lh-header picture {
    width: 45%;
  }

  // Enquadramento desktop é outro arquivo (1510x683), logo outra razão.
  .lh-header__img {
    aspect-ratio: auto 1510 / 683;
  }

  // Fogo e base acompanham a arte, que aqui dá ~500px (mobile ~250px) — mesma
  // proporção de 1,5x. O tile da máscara é fixo em px, então a textura do fogo
  // tem a mesma escala nos dois breakpoints.
  .lh-header::before {
    max-width: 750px;
  }

  .lh-header::after {
    max-width: 625px;
  }
}
</style>
